import React, { useEffect, useState } from "react";
import Button from "../../extra/Button";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import { useDispatch, useSelector } from "react-redux";
import { getSetting, updateSetting } from "../../store/setting/setting.action";
import { permissionError } from "../../../util/Alert";
import { usePermission } from "../../../hooks/usePermission";
import Skeleton from "react-loading-skeleton";
import { colors } from "../../../util/SkeletonColor";

const BODY_COLOR = 'style="color: #676565;"';

const normalizeBodyColor = (html = "") =>
  html.replace(/color:\s*rgb\(\s*187\s*,\s*187\s*,\s*187\s*\)/gi, "color: #676565");

const DEMO_PRIVACY_POLICY =
  `<h2>Privacy Policy for Era</h2> <h3>How We Use Your Information</h3><p><strong>Personal Information:</strong><span ${BODY_COLOR}> We use your email address for account management and to communicate with you about updates or important information.</span></p><p><strong>Usage Data:</strong><span ${BODY_COLOR}> We analyze usage patterns to improve our app's content and user experience.</span></p><p><strong>Device Information:</strong><span ${BODY_COLOR}> We use device information for technical purposes, such as troubleshooting and ensuring compatibility.</span></p><p><strong>Information Sharing:</strong><span ${BODY_COLOR}> We do not sell, trade, or otherwise transfer your personally identifiable information to third parties. We may share aggregated and anonymized data for statistical purposes.</span></p><p><strong>Security:</strong><span ${BODY_COLOR}> We take reasonable measures to protect your personal information from unauthorized access or disclosure. However, no method of transmission over the internet or electronic storage is completely secure.</span></p><p><strong>Your Choices:</strong><span ${BODY_COLOR}> You can manage your account settings and preferences within the app. You may opt out of promotional emails at any time by following the unsubscribe instructions in the email.</span></p><p><strong>Changes to This Privacy Policy:</strong><span ${BODY_COLOR}> We may update this Privacy Policy from time to time. Any changes will be posted on this page, and the "Last Updated" date will be revised accordingly.</span></p><p><strong>Contact Us:</strong></p><ol><li><span ${BODY_COLOR}>If you have any questions or concerns about this Privacy Policy, please contact us at [your contact email]. By using Era, you agree to the terms outlined in this Privacy Policy.</span></li></ol>`;

const DEMO_TERMS_AND_CONDITIONS =
  `<h2>Terms and Condition for Era</h2> <h3>How We Use Your Information</h3><p><strong>Personal Information:</strong><span ${BODY_COLOR}> We use your email address for account management and to communicate with you about updates or important information.</span></p><p><strong>Usage Data:</strong><span ${BODY_COLOR}> We analyze usage patterns to improve our app's content and user experience.</span></p><p><strong>Device Information:</strong><span ${BODY_COLOR}> We use device information for technical purposes, such as troubleshooting and ensuring compatibility.</span></p><p><strong>Information Sharing:</strong><span ${BODY_COLOR}> We do not sell, trade, or otherwise transfer your personally identifiable information to third parties. We may share aggregated and anonymized data for statistical purposes.</span></p><p><strong>Security:</strong><span ${BODY_COLOR}> We take reasonable measures to protect your personal information from unauthorized access or disclosure. However, no method of transmission over the internet or electronic storage is completely secure.</span></p><p><strong>Your Choices:</strong><span ${BODY_COLOR}> You can manage your account settings and preferences within the app. You may opt out of promotional emails at any time by following the unsubscribe instructions in the email.</span></p><p><strong>Contact Us:</strong></p><ol><li><span ${BODY_COLOR}>If you have any questions or concerns about this Terms and Conditions, please contact us at [your contact email]. By using Wallki, you agree to the terms outlined in these Terms and Conditions.</span></li></ol>`;

const Others = () => {


  const { can } = usePermission();
  const canEdit = can("Setting", "Edit");

  const dispatch = useDispatch();
  const { setting } = useSelector((state) => state.setting);
  const hasPermission = useSelector((state) => state.admin.admin.flag);
  const [active, setActive] = useState("Privacy");
  const [privacyPolicyLink, setPrivacyPolicyLink] = useState(DEMO_PRIVACY_POLICY);
  const [termsAndConditionsLink, setTermsAndConditionsLink] = useState(
    DEMO_TERMS_AND_CONDITIONS
  );
  const [loading, setLoading] = useState(true);

  const [error, setError] = useState({
    privacyPolicyLink: "",
    termsAndConditionsLink: "",
  });


  useEffect(() => {
    if (!hasPermission) return;
    dispatch(getSetting());
  }, [dispatch, hasPermission]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setLoading(false);
    }, 1500);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    if (!hasPermission) return;
    setPrivacyPolicyLink(
      normalizeBodyColor(setting?.privacyPolicyLink || DEMO_PRIVACY_POLICY)
    );
    setTermsAndConditionsLink(
      normalizeBodyColor(setting?.termsAndConditionsLink || DEMO_TERMS_AND_CONDITIONS)
    );
  }, [setting, hasPermission]);

  const handleSubmit = (e) => {
    e.preventDefault();

    if (!hasPermission) return permissionError();

    let errors = {};
    // Privacy Policy Link Validation
    if (!privacyPolicyLink || privacyPolicyLink.trim() === "" || privacyPolicyLink === "<p><br></p>") {
      errors.privacyPolicyLink = "Privacy Policy content cannot be empty.";
    }

    // Terms and Conditions Link Validation
    if (!termsAndConditionsLink || termsAndConditionsLink.trim() === "" || termsAndConditionsLink === "<p><br></p>") {
      errors.termsAndConditionsLink = "Terms and Conditions content cannot be empty.";
    }

    if (Object.keys(errors).length > 0) {
      setError(errors);
      return;
    }

    let settingData = {
      privacyPolicyLink,
      termsAndConditionsLink
    };

    dispatch(updateSetting(settingData, setting?._id))
  };

  return (
    <>
      <div className="sellerHeader primeHeader">
        <div
          style={{
            display: "flex",
            width: "fit-content",
            background: "#fafafd",
            borderRadius: "8px",
            border: "1px solid #ededed",
            margin: "10px 0",
          }}
        >
          
            <>
              <Button
                btnName="Privacy policy"
                newClass="themeFont"
                style={{
                  borderRadius: "8px 0 0 8px",
                  backgroundColor: active === "Privacy" ? "#f7dada" : "#fff",
                  color: active === "Privacy" ? "#b93160" : "#2f2b3db3",
                  border: "none",
                  fontWeight: "500",
                  padding: "10px 22px",
                  boxShadow: active === "Privacy" ? "0 0 4px #f7dada" : "none",
                  transition: "all 0.2s",
                  cursor: "pointer",
                }}
                onClick={() => setActive("Privacy")}
              />
              <Button
                btnName="Terms and condition"
                newClass="themeFont"
                style={{
                  borderRadius: "0 8px 8px 0",
                  backgroundColor: active === "Terms" ? "#f7dada" : "#fff",
                  color: active === "Terms" ? "#b93160" : "#2f2b3db3",
                  border: "none",
                  fontWeight: "500",
                  padding: "10px 22px",
                  boxShadow: active === "Terms" ? "0 0 4px #f7dada" : "none",
                  transition: "all 0.2s",
                  cursor: "pointer",
                }}
                onClick={() => setActive("Terms")}
              />
            </>
         
        </div>
        <div style={{ marginTop: "18px" }}>
          {loading ? (
            <Skeleton height={400} baseColor={colors?.baseColor} highlightColor={colors?.highlightColor} />
          ) : active === "Privacy" ? (
            <div key="privacy-tab">
              {/* <h4>Privacy Policy:</h4> */}
              <ReactQuill
                key="privacy-policy-editor"
                className="mt-4"
                theme="snow"
                value={privacyPolicyLink}
                onChange={(content) => {
                  setPrivacyPolicyLink(content);
                  if (error?.privacyPolicyLink) {
                    setError({ ...error, privacyPolicyLink: null });
                  }
                }}
                modules={{
                  toolbar: [
                    [{ header: [1, 2, 3, false] }],
                    ["bold", "italic", "underline", "strike", "blockquote"],
                    [{ list: "ordered" }, { list: "bullet" }],
                    ["link", "image"],
                    ["clean"],
                  ],
                }}
                placeholder="Write your Privacy Policy here..."
              />
              {error?.privacyPolicyLink && (
                <div style={{ color: "red", marginTop: "5px" }}>
                  {error.privacyPolicyLink}
                </div>
              )}
            </div>
          ) : (
            <div key="terms-tab">
              {/* <h4>Terms and Conditions:</h4> */}
              <ReactQuill
                key="terms-conditions-editor"
                className="mt-4"
                theme="snow"
                value={termsAndConditionsLink}
                onChange={(content) => {
                  setTermsAndConditionsLink(content);
                  if (error?.termsAndConditionsLink) {
                    setError({ ...error, termsAndConditionsLink: null });
                  }
                }}
                modules={{
                  toolbar: [
                    [{ header: [1, 2, 3, false] }],
                    ["bold", "italic", "underline", "strike", "blockquote"],
                    [{ list: "ordered" }, { list: "bullet" }],
                    ["link", "image"],
                    ["clean"],
                  ],
                }}
                placeholder="Write your Terms and Conditions here..."
              />
              {error?.termsAndConditionsLink && (
                <div style={{ color: "red", marginTop: "5px" }}>
                  {error.termsAndConditionsLink}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
      <div className="row mt-2">
        <div className="col-12 d-flex justify-content-end">
          {canEdit && (
            <Button
              newClass={`whiteFont`}
              btnName={`Submit`}
              btnColor={`#b93160`}
              style={{ width: "90px", borderRadius: "6px", color: "#fff", backgroundColor: "#b93160", marginRight: "30px" }}
              onClick={(e) => handleSubmit(e)}
            />
          )}
        </div>
      </div>
    </>
  );
};

export default Others;
